import type { StaffPaymentList, StaffPaymentListFilter } from "@lafayette/schemas";
import type { StaffPaymentDetailPayload } from "@lafayette/api-client";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { usePaymentsRuntime } from "./runtime";

// The reads of the Payments screens. Nothing stays in the cache after a screen lets go (`gcTime: 0`) and a failed read is not retried by
// the library: the screen shows the error and offers a retry. Every read runs again when its screen opens.

export const paymentKeys = {
  all: ["staff-payments"] as const,
  list: (filter: string | null) => ["staff-payments", "list", filter ?? "ALL"] as const,
  detail: (paymentRequestId: string) => ["staff-payments", "detail", paymentRequestId] as const,
  quotes: (requestId: string) => ["staff-payments", "quotes", requestId] as const,
};

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnMount: "always",
} as const;

const PAYMENTS_PAGE_SIZE = 25;

/** The list, page by page: `fetchNextPage` sends the opaque `nextCursor` of the last page back as `cursor`. */
export function usePaymentList(filter: StaffPaymentListFilter | null, enabled: boolean) {
  const { payments } = usePaymentsRuntime();
  return useInfiniteQuery({
    queryKey: paymentKeys.list(filter),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }): Promise<StaffPaymentList> =>
      payments.list(
        {
          ...(filter === null ? {} : { filter }),
          ...(pageParam === null ? {} : { cursor: pageParam }),
          limit: PAYMENTS_PAGE_SIZE,
        },
        { signal },
      ),
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    ...READ_OPTIONS,
  });
}

export function usePaymentDetail(paymentRequestId: string, enabled: boolean) {
  const { payments } = usePaymentsRuntime();
  return useQuery({
    queryKey: paymentKeys.detail(paymentRequestId),
    queryFn: ({ signal }): Promise<StaffPaymentDetailPayload> =>
      payments.get(paymentRequestId, { signal }),
    enabled: enabled && paymentRequestId !== "",
    ...READ_OPTIONS,
  });
}

/** The quotes of one concierge request (the quote panel of the request page). */
export function useRequestQuotes(requestId: string, enabled: boolean) {
  const { quotes } = usePaymentsRuntime();
  return useQuery({
    queryKey: paymentKeys.quotes(requestId),
    queryFn: ({ signal }) => quotes.list(requestId, { signal }),
    enabled: enabled && requestId !== "",
    ...READ_OPTIONS,
  });
}
